import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { LOGO_CREAM_URL, INSTAGRAM_URL } from "@/data/content";

const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer
      className="bg-olive text-cream"
      data-testid="main-footer"
    >
      {/* CTA band */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16">
        <Link
          to="/contact"
          data-testid="footer-cta"
          className="group block"
        >
          <span className="font-mono text-[0.7rem] uppercase tracking-[0.28em] text-sage">
            {t.footer.tagline}
          </span>
          <h2 className="font-serif font-light text-5xl sm:text-6xl md:text-7xl mt-6 leading-[0.95] flex items-center flex-wrap gap-x-6">
            {t.footer.cta}
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-4">
              →
            </span>
          </h2>
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-12">
        <div className="h-px w-full bg-cream/15 mb-12" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <img
              src={LOGO_CREAM_URL}
              alt="BY VAYA"
              className="h-14 w-auto object-contain"
              data-testid="footer-logo"
            />
            <p className="font-serif text-2xl mt-6 max-w-xs leading-snug text-cream/80">
              {t.footer.crafted}
            </p>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sage mb-5">
              {t.footer.nav}
            </p>
            <ul className="space-y-3">
              {[
                { to: "/about", label: t.nav.about },
                { to: "/services", label: t.nav.services },
                { to: "/projects", label: t.nav.projects },
                { to: "/contact", label: t.nav.contact },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-cream/70 hover:text-cream transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sage mb-5">
              {t.footer.connect}
            </p>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:marie.vayacreative@gmail.com"
                  className="text-sm text-cream/70 hover:text-cream transition-colors break-words"
                  data-testid="footer-email"
                >
                  marie.vayacreative@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/351924711317"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-cream/70 hover:text-cream transition-colors"
                  data-testid="footer-whatsapp"
                >
                  WhatsApp
                </a>
              </li>
              <li>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-cream/70 hover:text-cream transition-colors"
                  data-testid="footer-instagram"
                >
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/byvaya"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-cream/70 hover:text-cream transition-colors"
                  data-testid="footer-linkedin"
                >
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="h-px w-full bg-cream/15 mt-16 mb-6" />
        <div className="flex flex-col sm:flex-row justify-between gap-3 text-xs text-cream/50 uppercase tracking-[0.14em]">
          <span>© {year} BY VAYA</span>
          <span>{t.footer.rights}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
